import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Calculator as CalcIcon, Home, DollarSign, TrendingUp } from 'lucide-react';

export const Calculator = () => {
  const [homeValue, setHomeValue] = useState(400000);
  const [age, setAge] = useState(70);
  const [mortgageBalance, setMortgageBalance] = useState(100000);
  const [showResults, setShowResults] = useState(false);

  // Memoize slider values to prevent unnecessary re-renders
  const homeValueArray = useMemo(() => [homeValue], [homeValue]);
  const ageArray = useMemo(() => [age], [age]);
  const mortgageBalanceArray = useMemo(() => [mortgageBalance], [mortgageBalance]);

  // Simple calculation logic (mock)
  const calculateBenefits = () => {
    const equity = homeValue - mortgageBalance;
    const lendingLimit = Math.min(homeValue, 1149825); // 2024 FHA limit
    const agePercentage = 0.35 + ((age - 62) * 0.015); // Simplified percentage
    const principalLimit = lendingLimit * agePercentage;
    
    // Calculate closing costs
    const imip = lendingLimit * 0.02; // Initial Mortgage Insurance Premium (2%)
    
    // Origination fee: $2,500 or 2% of first $200k + 1% over (max $6,000)
    let originationFee;
    if (lendingLimit <= 125000) {
      originationFee = 2500;
    } else if (lendingLimit <= 200000) {
      originationFee = Math.min(lendingLimit * 0.02, 6000);
    } else {
      originationFee = Math.min(4000 + ((lendingLimit - 200000) * 0.01), 6000);
    }
    
    const thirdPartyCosts = 3000; // Appraisal, title, recording, etc.
    const totalClosingCosts = imip + originationFee + thirdPartyCosts;
    
    // Available funds after payoff and costs
    const availableFunds = Math.max(0, principalLimit - mortgageBalance - totalClosingCosts);
    
    return {
      equity,
      principalLimit: Math.round(principalLimit),
      availableFunds: Math.round(availableFunds),
      monthlySavings: Math.round(mortgageBalance * 0.006), // Assuming 6% annual interest
      imip: Math.round(imip),
      originationFee: Math.round(originationFee),
      thirdPartyCosts: Math.round(thirdPartyCosts),
      totalClosingCosts: Math.round(totalClosingCosts)
    };
  };

  const results = calculateBenefits();

  const handleCalculate = () => {
    setShowResults(true);
  };

  return (
    <section id="calculator" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <CalcIcon className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Reverse Mortgage Calculator
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get an instant estimate of how much you could receive from a reverse mortgage. 
            This is for educational purposes only—actual amounts may vary.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator Input */}
            <Card className="shadow-xl border-2">
              <CardHeader className="bg-blue-50">
                <CardTitle>Your Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Home Value */}
                <div className="space-y-3">
                  <Label htmlFor="homeValue" className="text-base font-semibold flex items-center">
                    <Home className="w-4 h-4 mr-2 text-blue-600" />
                    Estimated Home Value
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      id="homeValue"
                      type="number"
                      value={homeValue}
                      onChange={(e) => setHomeValue(Number(e.target.value))}
                      className="text-lg"
                    />
                  </div>
                  <Slider
                    value={homeValueArray}
                    onValueChange={(value) => setHomeValue(value[0])}
                    min={100000}
                    max={2000000}
                    step={10000}
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-600">
                    ${homeValue.toLocaleString()}
                  </p>
                </div>

                {/* Age */}
                <div className="space-y-3">
                  <Label htmlFor="age" className="text-base font-semibold">
                    Your Age (Youngest Borrower)
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      id="age"
                      type="number"
                      value={age}
                      onChange={(e) => setAge(Number(e.target.value))}
                      min={62}
                      max={100}
                      className="text-lg"
                    />
                  </div>
                  <Slider
                    value={ageArray}
                    onValueChange={(value) => setAge(value[0])}
                    min={62}
                    max={95}
                    step={1}
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-600">
                    {age} years old
                  </p>
                </div>

                {/* Mortgage Balance */}
                <div className="space-y-3">
                  <Label htmlFor="mortgage" className="text-base font-semibold flex items-center">
                    <DollarSign className="w-4 h-4 mr-2 text-blue-600" />
                    Current Mortgage Balance
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      id="mortgage"
                      type="number"
                      value={mortgageBalance}
                      onChange={(e) => setMortgageBalance(Number(e.target.value))}
                      className="text-lg"
                    />
                  </div>
                  <Slider
                    value={mortgageBalanceArray}
                    onValueChange={(value) => setMortgageBalance(value[0])}
                    min={0}
                    max={homeValue}
                    step={5000}
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-600">
                    ${mortgageBalance.toLocaleString()}
                  </p>
                </div>

                <Button 
                  onClick={handleCalculate}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                >
                  Calculate My Benefits
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className={`shadow-xl border-2 ${showResults ? 'border-blue-300 bg-blue-50' : ''}`}>
              <CardHeader className="bg-blue-600 text-white">
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Your Estimated Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!showResults ? (
                  <div className="h-full flex items-center justify-center py-12">
                    <div className="text-center">
                      <CalcIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">
                        Enter your information and click calculate to see your estimated benefits.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Result Cards */}
                    <div className="bg-white rounded-lg p-5 shadow-md border-2 border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Estimated Available Funds</p>
                      <p className="text-4xl font-bold text-blue-600">
                        ${results.availableFunds.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        After paying off mortgage and closing costs
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-5 shadow-md">
                      <p className="text-sm text-gray-600 mb-1">Principal Limit</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${results.principalLimit.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-5 shadow-md">
                      <p className="text-sm text-gray-600 mb-1">Current Home Equity</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${results.equity.toLocaleString()}
                      </p>
                    </div>

                    {/* Closing Costs Breakdown */}
                    <div className="bg-blue-50 rounded-lg p-5 border-2 border-blue-200">
                      <p className="text-sm font-semibold text-gray-800 mb-3">Estimated Closing Costs</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Initial Mortgage Insurance (2%)</span>
                          <span className="font-semibold text-gray-900">${results.imip.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Origination Fee</span>
                          <span className="font-semibold text-gray-900">${results.originationFee.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Third-Party Costs</span>
                          <span className="font-semibold text-gray-900">${results.thirdPartyCosts.toLocaleString()}</span>
                        </div>
                        <div className="border-t border-blue-300 pt-2 mt-2 flex justify-between">
                          <span className="font-semibold text-gray-800">Total Closing Costs</span>
                          <span className="font-bold text-blue-600">${results.totalClosingCosts.toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        * Most closing costs can be financed into the loan
                      </p>
                    </div>

                    {results.monthlySavings > 0 && (
                      <div className="bg-green-50 rounded-lg p-5 border-2 border-green-200">
                        <p className="text-sm text-green-800 mb-1">Est. Monthly Payment Savings</p>
                        <p className="text-2xl font-bold text-green-600">
                          ${results.monthlySavings.toLocaleString()}/mo
                        </p>
                      </div>
                    )}

                    {/* Disclaimer */}
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <p className="text-xs text-gray-700 leading-relaxed">
                        <strong>Important:</strong> This is an estimate only. Actual loan amounts 
                        depend on interest rates, property appraisal, and lender-specific 
                        guidelines. Closing costs shown are typical ranges and may vary. 
                        Schedule a consultation for an accurate assessment.
                      </p>
                    </div>

                    <Button 
                      onClick={() => {
                        const element = document.getElementById('contact');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Get Your Personalized Quote
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
